import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  getDocsFromCache,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  where,
  runTransaction,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { auth, db, storage } from "../config/firebase";
import {
  emailService,
  PaymentConfirmationData,
} from "./emailService";

// Types
export interface User {
  id: string;
  email: string;
  displayName?: string;
}

export interface WorkshopDate {
  id: string;
  date: string;
  time: string;
  maxParticipants: number;
  currentParticipants: number;
  isAvailable: boolean;
}

export interface Workshop {
  id?: string;
  title: string;
  titleRu: string;
  description: string;
  descriptionRu: string;
  dates: WorkshopDate[];
  location: string;
  locationRu: string;
  price: number;
  imageUrl?: string;
  displayOrder?: number;
  createdAt?: any;
  updatedAt?: any;
}

export interface BlogPost {
  id?: string;
  title: string;
  titleRu: string;
  content: string;
  contentRu: string;
  imageUrl?: string;
  publishedAt?: any;
  createdAt?: any;
  updatedAt?: any;
}

export interface MediaFile {
  id?: string;
  name: string;
  url: string;
  type: string;
  size: number;
  uploadedAt?: any;
  category?: "gallery" | "blog" | "workshop";
  displayOrder?: number;
}

export interface Settings {
  id?: string;
  siteName: string;
  siteDescription: string;
  contactEmail: string;
  contactPhone: string;
  fromEmail?: string; // כתובת מייל שולח למיילים אוטומטיים ללקוחות
  voucherPricePerPerson?: number; // מחיר שובר מתנה לאדם (נקבע על ידי האדמין)
  socialMedia: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  updatedAt?: any;
}

export interface ContactMessage {
  id?: string;
  name: string;
  email: string;
  message: string;
  createdAt?: any;
  read?: boolean;
}

export interface WorkshopRegistration {
  id?: string;
  workshopId: string;
  workshopTitle: string;
  workshopDateId: string;
  workshopDate: string;
  workshopTime: string;
  name: string;
  email: string;
  phone: string;
  ticketsCount: number;
  totalAmount: number;
  language?: 'he' | 'ru';
  createdAt?: any;
  status?: "pending" | "confirmed" | "cancelled";
  paymentStatus?: "pending" | "paid" | "failed" | "cancelled";
  transactionId?: string;
  orderId?: string;
  amount?: number;
  voucherCode?: string; // קוד שובר מתנה ששימש בהרשמה
  voucherParticipantsCovered?: number; // כמות משתתפים שכוסו על ידי השובר
  discountAmount?: number; // סכום ההנחה שניתן על ידי השובר
  participantCountUpdated?: boolean; // האם מספר המשתתפים כבר עודכן בסדנה
  confirmationEmailSent?: boolean; // האם מייל אישור תשלום כבר נשלח
}

export interface GiftVoucher {
  id?: string;
  code: string; // קוד מימוש ייחודי (לדוגמה GIFT-XXXX-XXXX)
  participants: number; // כמות המשתתפים שהשובר מכסה
  pricePerPerson: number; // מחיר לאדם בעת הרכישה (snapshot)
  amountPaid: number; // סכום ששולם על השובר
  purchaserName: string;
  purchaserEmail: string;
  purchaserPhone: string;
  status: "pending" | "active" | "redeemed";
  paymentStatus: "pending" | "paid" | "failed" | "cancelled";
  transactionId?: string;
  orderId?: string;
  redeemedAt?: any;
  redeemedByRegistrationId?: string;
  createdAt?: any;
  language?: "he" | "ru";
  confirmationEmailSent?: boolean; // האם מייל השובר כבר נשלח (חד-פעמי)
}

// Auth services
export const authService = {
  login: async (email: string, password: string) => {
    if (!auth) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return {
      user: {
        id: userCredential.user.uid,
        email: userCredential.user.email!,
        displayName: userCredential.user.displayName,
      },
      token: await userCredential.user.getIdToken(),
    };
  },

  logout: async () => {
    if (!auth) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    await signOut(auth);
  },

  onAuthStateChanged: (callback: (user: FirebaseUser | null) => void) => {
    if (!auth) {
            return () => {};
    }
    return onAuthStateChanged(auth, callback);
  },

  getCurrentUser: () => {
    if (!auth) {
      return null;
    }
    return auth.currentUser;
  },
};

// Workshop services
// Short-lived in-memory cache + in-flight dedup so the many components that each
// call getAll() on a single page share ONE network read instead of flooding
// Firestore (which stalls long-polling on Safari).
const WORKSHOPS_MEMO_TTL_MS = 30000;
let workshopsMemo: { data: Workshop[]; at: number } | null = null;
let workshopsInFlight: Promise<Workshop[]> | null = null;
const invalidateWorkshopsCache = (): void => {
  workshopsMemo = null;
  workshopsInFlight = null;
};

const sortWorkshops = (workshops: Workshop[]): Workshop[] =>
  workshops.sort((a, b) => {
    if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
      return a.displayOrder - b.displayOrder;
    }
    if (a.displayOrder !== undefined) return -1;
    if (b.displayOrder !== undefined) return 1;
    const timeA =
      a.createdAt?.toMillis?.() ?? new Date(a.createdAt).getTime() ?? 0;
    const timeB =
      b.createdAt?.toMillis?.() ?? new Date(b.createdAt).getTime() ?? 0;
    return timeB - timeA;
  });

export const workshopService = {
  getAll: async (): Promise<Workshop[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const now = Date.now();
    if (workshopsMemo && now - workshopsMemo.at < WORKSHOPS_MEMO_TTL_MS) {
      return workshopsMemo.data;
    }
    if (workshopsInFlight) {
      return workshopsInFlight;
    }

    const workshopsRef = collection(db, "workshops");
    workshopsInFlight = (async () => {
      const querySnapshot = await getDocs(workshopsRef);
      const workshops = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Workshop[];
      const sorted = sortWorkshops(workshops);
      workshopsMemo = { data: sorted, at: Date.now() };
      return sorted;
    })();

    try {
      return await workshopsInFlight;
    } finally {
      workshopsInFlight = null;
    }
  },

  getById: async (id: string): Promise<Workshop> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "workshops", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Workshop not found");
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as Workshop;
  },

  create: async (workshopData: Omit<Workshop, "id">): Promise<Workshop> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const workshopsRef = collection(db, "workshops");

    // אם לא נקבע displayOrder, נקבל את המקסימום ונוסיף 1
    let displayOrder = workshopData.displayOrder;
    if (displayOrder === undefined) {
      const allWorkshops = await getDocs(workshopsRef);
      const orders = allWorkshops.docs
        .map((doc) => doc.data().displayOrder)
        .filter(
          (order) =>
            order !== undefined && order !== null && typeof order === "number"
        );
      displayOrder =
        orders.length > 0 ? Math.max(...orders) + 1 : allWorkshops.size;
    }

    const docRef = await addDoc(workshopsRef, {
      ...workshopData,
      displayOrder,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    invalidateWorkshopsCache();

    return {
      id: docRef.id,
      ...workshopData,
      displayOrder,
    } as Workshop;
  },

  update: async (
    id: string,
    workshopData: Partial<Workshop>
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "workshops", id);
    await updateDoc(docRef, {
      ...workshopData,
      updatedAt: serverTimestamp(),
    });
    invalidateWorkshopsCache();
  },

  delete: async (id: string): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "workshops", id);
    await deleteDoc(docRef);
    invalidateWorkshopsCache();
  },

  // New methods for managing workshop dates
  addWorkshopDate: async (
    workshopId: string,
    dateData: Omit<WorkshopDate, "id" | "currentParticipants">
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const workshopRef = doc(db, "workshops", workshopId);
    const workshopSnap = await getDoc(workshopRef);

    if (!workshopSnap.exists()) {
      throw new Error("Workshop not found");
    }

    const workshop = workshopSnap.data() as Workshop;
    const newDate: WorkshopDate = {
      id: Date.now().toString(), // Simple ID generation
      ...dateData,
      currentParticipants: 0,
    };

    const updatedDates = [...(workshop.dates || []), newDate];

    await updateDoc(workshopRef, {
      dates: updatedDates,
      updatedAt: serverTimestamp(),
    });
    invalidateWorkshopsCache();
  },

  updateWorkshopDate: async (
    workshopId: string,
    dateId: string,
    dateData: Partial<WorkshopDate>
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const workshopRef = doc(db, "workshops", workshopId);
    const workshopSnap = await getDoc(workshopRef);

    if (!workshopSnap.exists()) {
      throw new Error("Workshop not found");
    }

    const workshop = workshopSnap.data() as Workshop;
    const updatedDates = workshop.dates.map((date) => {
      if (date.id === dateId) {
        const updatedDate = { ...date, ...dateData };

        // Update availability based on current participants and max participants
        if (dateData.maxParticipants !== undefined) {
          updatedDate.isAvailable =
            updatedDate.currentParticipants < updatedDate.maxParticipants;
        }

        return updatedDate;
      }
      return date;
    });

    await updateDoc(workshopRef, {
      dates: updatedDates,
      updatedAt: serverTimestamp(),
    });
    invalidateWorkshopsCache();
  },

  deleteWorkshopDate: async (
    workshopId: string,
    dateId: string
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const workshopRef = doc(db, "workshops", workshopId);
    const workshopSnap = await getDoc(workshopRef);

    if (!workshopSnap.exists()) {
      throw new Error("Workshop not found");
    }

    const workshop = workshopSnap.data() as Workshop;
    const updatedDates = workshop.dates.filter((date) => date.id !== dateId);

    await updateDoc(workshopRef, {
      dates: updatedDates,
      updatedAt: serverTimestamp(),
    });
    invalidateWorkshopsCache();
  },

  // Method to update participant count when registration is made
  updateParticipantCount: async (
    workshopId: string,
    dateId: string,
    increment: boolean,
    ticketsCount: number = 1
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const workshopRef = doc(db, "workshops", workshopId);
    const workshopSnap = await getDoc(workshopRef);

    if (!workshopSnap.exists()) {
      throw new Error("Workshop not found");
    }

    const workshop = workshopSnap.data() as Workshop;
    let dateFound = false;
    const updatedDates = workshop.dates.map((date) => {
      if (date.id === dateId) {
        dateFound = true;
        const newCount = increment
          ? date.currentParticipants + ticketsCount
          : Math.max(0, date.currentParticipants - ticketsCount);

        return {
          ...date,
          currentParticipants: newCount,
          isAvailable: newCount < date.maxParticipants,
        };
      }
      return date;
    });

    if (!dateFound) {
      throw new Error(`Workshop date not found: ${dateId}`);
    }

    await updateDoc(workshopRef, {
      dates: updatedDates,
      updatedAt: serverTimestamp(),
    });
    invalidateWorkshopsCache();
  },

  // פונקציה לעדכון סדר תצוגה של סדנה אחת
  updateDisplayOrder: async (id: string, newOrder: number): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "workshops", id);
    await updateDoc(docRef, {
      displayOrder: newOrder,
      updatedAt: serverTimestamp(),
    });
    invalidateWorkshopsCache();
  },

  // פונקציה לעדכון סדר כל הסדנאות בבת אחת
  reorderWorkshops: async (
    reorderMap: { id: string; displayOrder: number }[]
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    // TypeScript לא מזהה שהבדיקה למעלה מבטיחה ש-db לא null, אז נשתמש ב-non-null assertion
    const firestoreDb = db;

    const updatePromises = reorderMap.map(({ id, displayOrder }) => {
      const docRef = doc(firestoreDb, "workshops", id);
      return updateDoc(docRef, {
        displayOrder,
        updatedAt: serverTimestamp(),
      });
    });

    await Promise.all(updatePromises);
    invalidateWorkshopsCache();
  },
};

// Blog services
// Cache-first read + in-memory memo + in-flight dedup. The persistent local
// cache is read first so a stalled Firestore long-polling channel (observed to
// hang ~21s on Safari) can never freeze the page once data has loaded once; the
// server read refreshes the cache in the background.
const BLOG_MEMO_TTL_MS = 30000;
let blogMemo: { data: BlogPost[]; at: number } | null = null;
let blogInFlight: Promise<BlogPost[]> | null = null;
const invalidateBlogCache = (): void => {
  blogMemo = null;
  blogInFlight = null;
};

export const blogService = {
  getAll: async (): Promise<BlogPost[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const now = Date.now();
    if (blogMemo && now - blogMemo.at < BLOG_MEMO_TTL_MS) {
      return blogMemo.data;
    }
    if (blogInFlight) {
      return blogInFlight;
    }

    const blogRef = collection(db, "blog");
    const q = query(blogRef, orderBy("createdAt", "desc"));

    const mapDocs = (snap: { docs: { id: string; data: () => unknown }[] }) =>
      snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) })) as BlogPost[];

    blogInFlight = (async () => {
      // Try the persistent cache first for an instant, stall-proof render.
      try {
        const cacheSnap = await getDocsFromCache(q);
        if (!cacheSnap.empty) {
          const cached = mapDocs(cacheSnap);
          blogMemo = { data: cached, at: Date.now() };
          // Refresh from server in the background (don't block the UI).
          getDocs(q)
            .then((fresh) => {
              blogMemo = { data: mapDocs(fresh), at: Date.now() };
            })
            .catch(() => {});
          return cached;
        }
      } catch {
        /* cache miss / unavailable -> fall through to server read */
      }

      const querySnapshot = await getDocs(q);
      const data = mapDocs(querySnapshot);
      blogMemo = { data, at: Date.now() };
      return data;
    })();

    try {
      return await blogInFlight;
    } finally {
      blogInFlight = null;
    }
  },

  getById: async (id: string): Promise<BlogPost> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "blog", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Blog post not found");
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as BlogPost;
  },

  create: async (postData: Omit<BlogPost, "id">): Promise<BlogPost> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const blogRef = collection(db, "blog");
    const docRef = await addDoc(blogRef, {
      ...postData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    invalidateBlogCache();

    return {
      id: docRef.id,
      ...postData,
    } as BlogPost;
  },

  update: async (id: string, postData: Partial<BlogPost>): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "blog", id);
    await updateDoc(docRef, {
      ...postData,
      updatedAt: serverTimestamp(),
    });
    invalidateBlogCache();
  },

  delete: async (id: string): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "blog", id);
    await deleteDoc(docRef);
    invalidateBlogCache();
  },
};

// Media services
// Cache-first read + in-memory memo + in-flight dedup (see blogService above).
const MEDIA_MEMO_TTL_MS = 30000;
let mediaMemo: { data: MediaFile[]; at: number } | null = null;
let mediaInFlight: Promise<MediaFile[]> | null = null;
const invalidateMediaCache = (): void => {
  mediaMemo = null;
  mediaInFlight = null;
};

const sortMedia = (files: MediaFile[]): MediaFile[] =>
  files.sort((a, b) => {
    if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
      return a.displayOrder - b.displayOrder;
    }
    if (a.displayOrder !== undefined) return -1;
    if (b.displayOrder !== undefined) return 1;
    const timeA =
      a.uploadedAt?.toMillis?.() ?? new Date(a.uploadedAt).getTime() ?? 0;
    const timeB =
      b.uploadedAt?.toMillis?.() ?? new Date(b.uploadedAt).getTime() ?? 0;
    return timeB - timeA;
  });

export const mediaService = {
  getAll: async (): Promise<MediaFile[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const now = Date.now();
    if (mediaMemo && now - mediaMemo.at < MEDIA_MEMO_TTL_MS) {
      return mediaMemo.data;
    }
    if (mediaInFlight) {
      return mediaInFlight;
    }

    const mediaRef = collection(db, "media");

    const mapDocs = (snap: { docs: { id: string; data: () => unknown }[] }) =>
      sortMedia(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) })) as MediaFile[]
      );

    mediaInFlight = (async () => {
      // Try the persistent cache first for an instant, stall-proof render.
      try {
        const cacheSnap = await getDocsFromCache(mediaRef);
        if (!cacheSnap.empty) {
          const cached = mapDocs(cacheSnap);
          mediaMemo = { data: cached, at: Date.now() };
          getDocs(mediaRef)
            .then((fresh) => {
              mediaMemo = { data: mapDocs(fresh), at: Date.now() };
            })
            .catch(() => {});
          return cached;
        }
      } catch {
        /* cache miss / unavailable -> fall through to server read */
      }

      const querySnapshot = await getDocs(mediaRef);
      const data = mapDocs(querySnapshot);
      mediaMemo = { data, at: Date.now() };
      return data;
    })();

    try {
      return await mediaInFlight;
    } finally {
      mediaInFlight = null;
    }
  },

  upload: async (
    file: File,
    category: "gallery" | "blog" | "workshop" = "gallery"
  ): Promise<MediaFile> => {
    if (!storage || !db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    try {
      // יצירת שם קובץ ייחודי
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name.replace(
        /[^a-zA-Z0-9.-]/g,
        "_"
      )}`;
      const storageRef = ref(storage, `media/${fileName}`);

      // העלאת הקובץ
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // שמירת המידע במסד הנתונים
      const mediaRef = collection(db, "media");

      // אם לא נקבע displayOrder, נקבל את המקסימום ונוסיף 1
      // אבל רק אם יש תמונות עם displayOrder תקין (1 ומעלה)
      const allMedia = await getDocs(mediaRef);
      const orders = allMedia.docs
        .map((doc) => doc.data().displayOrder)
        .filter(
          (order) =>
            order !== undefined &&
            order !== null &&
            typeof order === "number" &&
            order > 0 // רק displayOrder תקין (1 ומעלה)
        );
      const displayOrder =
        orders.length > 0 ? Math.max(...orders) + 1 : allMedia.size + 1;

      const docRef = await addDoc(mediaRef, {
        name: file.name,
        url: downloadURL,
        type: file.type,
        size: file.size,
        category: category,
        displayOrder,
        uploadedAt: serverTimestamp(),
      });
      invalidateMediaCache();

      return {
        id: docRef.id,
        name: file.name,
        url: downloadURL,
        type: file.type,
        size: file.size,
        category: category,
        displayOrder,
      } as MediaFile;
    } catch (error) {
            throw new Error(
        `Failed to upload file: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  },

  delete: async (id: string): Promise<void> => {
    if (!db || !storage) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    try {
      const docRef = doc(db, "media", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as MediaFile;

        // מחיקת הקובץ מ-Storage
        if (data.url) {
          try {
            const storageRef = ref(storage, data.url);
            await deleteObject(storageRef);
          } catch (storageError) {
                        // ממשיך למחיקה מהמסד נתונים גם אם המחיקה מ-Storage נכשלה
          }
        }

        // מחיקה מהמסד נתונים
        await deleteDoc(docRef);
        invalidateMediaCache();
      }
    } catch (error) {
            throw new Error(
        `Failed to delete file: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  },

  // פונקציה חדשה לבדיקת קישור תמונה
  validateImageUrl: async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      return response.ok;
    } catch (error) {
            return false;
    }
  },

  // פונקציה לעדכון סדר תצוגה של תמונה אחת
  updateDisplayOrder: async (id: string, newOrder: number): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "media", id);
    await updateDoc(docRef, {
      displayOrder: newOrder,
      updatedAt: serverTimestamp(),
    });
    invalidateMediaCache();
  },

  // פונקציה לעדכון סדר כל התמונות בבת אחת
  reorderMedia: async (
    reorderMap: { id: string; displayOrder: number }[]
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const firestoreDb = db;

    const updatePromises = reorderMap.map(({ id, displayOrder }) => {
      const docRef = doc(firestoreDb, "media", id);
      return updateDoc(docRef, {
        displayOrder,
        updatedAt: serverTimestamp(),
      });
    });

    await Promise.all(updatePromises);
    invalidateMediaCache();
  },
};

// Settings services
// Short-lived in-memory cache + in-flight dedup: settings are read by many
// components on every page; without this they each issue a separate network
// read, flooding Firestore long-polling.
const SETTINGS_MEMO_TTL_MS = 60000;
let settingsMemo: { data: Settings; at: number } | null = null;
let settingsInFlight: Promise<Settings> | null = null;
const invalidateSettingsCache = (): void => {
  settingsMemo = null;
  settingsInFlight = null;
};

export const settingsService = {
  get: async (): Promise<Settings> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const now = Date.now();
    if (settingsMemo && now - settingsMemo.at < SETTINGS_MEMO_TTL_MS) {
      return settingsMemo.data;
    }
    if (settingsInFlight) {
      return settingsInFlight;
    }

    const settingsRef = collection(db, "settings");
    settingsInFlight = (async () => {
      const querySnapshot = await getDocs(settingsRef);

      let result: Settings;
      if (querySnapshot.empty) {
        result = {
          siteName: "Shmuel",
          siteDescription: "Default site description",
          contactEmail: "",
          contactPhone: "",
          socialMedia: {},
        };
      } else {
        const settingsDoc = querySnapshot.docs[0];
        result = {
          id: settingsDoc.id,
          ...settingsDoc.data(),
        } as Settings;
      }
      settingsMemo = { data: result, at: Date.now() };
      return result;
    })();

    try {
      return await settingsInFlight;
    } finally {
      settingsInFlight = null;
    }
  },

  update: async (settingsData: Partial<Settings>): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const settingsRef = collection(db, "settings");
    const querySnapshot = await getDocs(settingsRef);

    if (querySnapshot.empty) {
      // Create new settings document
      await addDoc(settingsRef, {
        ...settingsData,
        updatedAt: serverTimestamp(),
      });
    } else {
      // Update existing settings document
      const docRef = doc(db, "settings", querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        ...settingsData,
        updatedAt: serverTimestamp(),
      });
    }
    invalidateSettingsCache();
  },
};

// Contact services
export const contactService = {
  getAll: async (): Promise<ContactMessage[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const contactRef = collection(db, "contact");
    const q = query(contactRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as ContactMessage[];
  },

  getById: async (id: string): Promise<ContactMessage> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "contact", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Contact message not found");
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as ContactMessage;
  },

  create: async (
    messageData: Omit<ContactMessage, "id">
  ): Promise<ContactMessage> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const contactRef = collection(db, "contact");
    const docRef = await addDoc(contactRef, {
      ...messageData,
      createdAt: serverTimestamp(),
      read: false,
    });

    return {
      id: docRef.id,
      ...messageData,
    } as ContactMessage;
  },

  markAsRead: async (id: string): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "contact", id);
    await updateDoc(docRef, {
      read: true,
    });
  },

  delete: async (id: string): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "contact", id);
    await deleteDoc(docRef);
  },
};

// Workshop Registration services
export const registrationService = {
  getAll: async (): Promise<WorkshopRegistration[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const registrationsRef = collection(db, "registrations");
    const q = query(registrationsRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as WorkshopRegistration[];
  },

  getByWorkshopId: async (
    workshopId: string
  ): Promise<WorkshopRegistration[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const registrationsRef = collection(db, "registrations");
    const q = query(
      registrationsRef,
      where("workshopId", "==", workshopId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as WorkshopRegistration[];
  },

  getByWorkshopDateId: async (
    workshopDateId: string
  ): Promise<WorkshopRegistration[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const registrationsRef = collection(db, "registrations");
    const q = query(
      registrationsRef,
      where("workshopDateId", "==", workshopDateId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as WorkshopRegistration[];
  },

  create: async (
    registrationData: Omit<WorkshopRegistration, "id">
  ): Promise<WorkshopRegistration> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const registrationsRef = collection(db, "registrations");
    const docRef = await addDoc(registrationsRef, {
      ...registrationData,
      createdAt: serverTimestamp(),
      status: "pending",
      paymentStatus: "pending",
    });

    // Note: Participant count will be updated only when payment is successful
    // This prevents blocking spots for unpaid registrations

    return {
      id: docRef.id,
      ...registrationData,
    } as WorkshopRegistration;
  },

  updateStatus: async (
    id: string,
    status: "pending" | "confirmed" | "cancelled"
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "registrations", id);
    await updateDoc(docRef, {
      status,
    });
  },

  delete: async (id: string): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    // Get registration data before deletion to update participant count
    try {
      const registration = await registrationService.getById(id);
      if (registration && registration.workshopDateId) {
        await workshopService.updateParticipantCount(
          registration.workshopId,
          registration.workshopDateId,
          false,
          registration.ticketsCount
        );
      }
    } catch (error) {
          }

    const docRef = doc(db, "registrations", id);
    await deleteDoc(docRef);
  },

  updatePaymentStatus: async (
    id: string,
    paymentStatus: "pending" | "paid" | "failed" | "cancelled",
    transactionId?: string,
    orderId?: string,
    amount?: number
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "registrations", id);
    const updateData: any = { paymentStatus };

    if (transactionId) updateData.transactionId = transactionId;
    if (orderId) updateData.orderId = orderId;
    if (amount) updateData.amount = amount;

    await updateDoc(docRef, updateData);
  },

  /** מעדכן מקומות תפוסים בסדנה פעם אחת בלבד (idempotent, atomic) */
  confirmRegistrationSpots: async (registrationId: string): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const dbRef = db;
    const regRef = doc(dbRef, "registrations", registrationId);

    await runTransaction(dbRef, async (transaction) => {
      const regSnap = await transaction.get(regRef);
      if (!regSnap.exists()) {
        throw new Error("Registration not found");
      }

      const registration = {
        id: regSnap.id,
        ...regSnap.data(),
      } as WorkshopRegistration;

      if (registration.participantCountUpdated) {
        return;
      }

      if (!registration.workshopDateId) {
        throw new Error("Registration is missing workshopDateId");
      }

      const workshopRef = doc(dbRef, "workshops", registration.workshopId);
      const workshopSnap = await transaction.get(workshopRef);

      if (!workshopSnap.exists()) {
        throw new Error("Workshop not found");
      }

      const workshop = workshopSnap.data() as Workshop;
      const ticketsCount = registration.ticketsCount || 1;
      let dateFound = false;

      const updatedDates = workshop.dates.map((date) => {
        if (date.id === registration.workshopDateId) {
          dateFound = true;
          const newCount = date.currentParticipants + ticketsCount;
          return {
            ...date,
            currentParticipants: newCount,
            isAvailable: newCount < date.maxParticipants,
          };
        }
        return date;
      });

      if (!dateFound) {
        throw new Error(
          `Workshop date not found: ${registration.workshopDateId}`
        );
      }

      transaction.update(workshopRef, {
        dates: updatedDates,
        updatedAt: serverTimestamp(),
      });
      transaction.update(regRef, {
        participantCountUpdated: true,
      });
    });
  },

  /** שולח מייל אישור תשלום פעם אחת בלבד (idempotent, atomic) */
  sendConfirmationEmailIfNeeded: async (
    registrationId: string,
    emailData: PaymentConfirmationData
  ): Promise<boolean> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const dbRef = db;
    const regRef = doc(dbRef, "registrations", registrationId);
    let shouldSend = false;

    await runTransaction(dbRef, async (transaction) => {
      const regSnap = await transaction.get(regRef);
      if (!regSnap.exists()) {
        throw new Error("Registration not found");
      }

      const registration = regSnap.data() as WorkshopRegistration;
      if (registration.confirmationEmailSent) {
        return;
      }

      shouldSend = true;
      transaction.update(regRef, {
        confirmationEmailSent: true,
      });
    });

    if (shouldSend) {
      await emailService.sendPaymentConfirmation(emailData);
      return true;
    }

    return false;
  },

  getByTransactionId: async (
    transactionId: string
  ): Promise<WorkshopRegistration | null> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const registrationsRef = collection(db, "registrations");
    const q = query(
      registrationsRef,
      where("transactionId", "==", transactionId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    } as WorkshopRegistration;
  },

  getById: async (id: string): Promise<WorkshopRegistration> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "registrations", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Registration not found");
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as WorkshopRegistration;
  },
};

// Gift voucher services
const VOUCHER_CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // ללא תווים מבלבלים

const generateVoucherCode = (): string => {
  const block = (length: number): string => {
    let result = "";
    for (let i = 0; i < length; i += 1) {
      result += VOUCHER_CODE_CHARS.charAt(
        Math.floor(Math.random() * VOUCHER_CODE_CHARS.length)
      );
    }
    return result;
  };
  return `GIFT-${block(4)}-${block(4)}`;
};

export const giftVoucherService = {
  createPurchase: async (
    voucherData: Omit<
      GiftVoucher,
      "id" | "code" | "status" | "paymentStatus" | "createdAt"
    >
  ): Promise<GiftVoucher> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const vouchersRef = collection(db, "giftVouchers");
    const docRef = await addDoc(vouchersRef, {
      ...voucherData,
      code: "",
      status: "pending",
      paymentStatus: "pending",
      createdAt: serverTimestamp(),
    });

    return {
      id: docRef.id,
      code: "",
      status: "pending",
      paymentStatus: "pending",
      ...voucherData,
    } as GiftVoucher;
  },

  getById: async (id: string): Promise<GiftVoucher> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "giftVouchers", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Gift voucher not found");
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as GiftVoucher;
  },

  getByCode: async (code: string): Promise<GiftVoucher | null> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const vouchersRef = collection(db, "giftVouchers");
    const q = query(vouchersRef, where("code", "==", code.trim().toUpperCase()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const docSnap = querySnapshot.docs[0];
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as GiftVoucher;
  },

  updatePaymentStatus: async (
    id: string,
    paymentStatus: "pending" | "paid" | "failed" | "cancelled",
    transactionId?: string,
    orderId?: string
  ): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "giftVouchers", id);
    const updateData: any = { paymentStatus };

    if (transactionId) updateData.transactionId = transactionId;
    if (orderId) updateData.orderId = orderId;

    await updateDoc(docRef, updateData);
  },

  // מסמן את השובר כמשולם ומפעיל אותו: מייצר קוד ייחודי אם עוד לא קיים
  activate: async (id: string, transactionId?: string): Promise<GiftVoucher> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const docRef = doc(db, "giftVouchers", id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error("Gift voucher not found");
    }

    const existing = docSnap.data() as GiftVoucher;

    // אם השובר כבר הופעל וקיבל קוד - מחזירים אותו כמו שהוא (idempotent)
    if (existing.status === "active" && existing.code) {
      return { id: docSnap.id, ...existing } as GiftVoucher;
    }

    // ייצור קוד ייחודי (ניסיונות עד שמוצאים קוד פנוי)
    let code = existing.code;
    if (!code) {
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const candidate = generateVoucherCode();
        const existingWithCode = await giftVoucherService.getByCode(candidate);
        if (!existingWithCode) {
          code = candidate;
          break;
        }
      }
      if (!code) {
        code = `${generateVoucherCode()}-${Date.now().toString(36).toUpperCase()}`;
      }
    }

    const updateData: any = {
      code,
      status: "active",
      paymentStatus: "paid",
    };
    if (transactionId) updateData.transactionId = transactionId;

    await updateDoc(docRef, updateData);

    return {
      id: docSnap.id,
      ...existing,
      ...updateData,
    } as GiftVoucher;
  },

  // ניסיון "לתפוס" את שליחת מייל השובר באופן אטומי. מחזיר true רק אם הקורא הזה
  // הוא שהפך את confirmationEmailSent מ-false ל-true, כך שגם הצד-לקוח וגם הצד-שרת
  // (paymentCallback) משתמשים באותו דגל ולעולם לא נשלח מייל כפול.
  claimConfirmationEmail: async (id: string): Promise<boolean> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }

    const dbRef = db;
    const voucherRef = doc(dbRef, "giftVouchers", id);
    let shouldSend = false;

    await runTransaction(dbRef, async (transaction) => {
      const snap = await transaction.get(voucherRef);
      if (!snap.exists()) {
        return;
      }

      const voucher = snap.data() as GiftVoucher;
      if (voucher.confirmationEmailSent) {
        return;
      }

      shouldSend = true;
      transaction.update(voucherRef, {
        confirmationEmailSent: true,
      });
    });

    return shouldSend;
  },

  // בדיקת תקינות קוד שובר למימוש בהרשמה
  validateCode: async (
    code: string
  ): Promise<{ valid: boolean; voucher?: GiftVoucher; error?: string }> => {
    const voucher = await giftVoucherService.getByCode(code);

    if (!voucher) {
      return { valid: false, error: "notFound" };
    }
    if (voucher.paymentStatus !== "paid" || voucher.status === "pending") {
      return { valid: false, error: "notActive" };
    }
    if (voucher.status === "redeemed") {
      return { valid: false, error: "alreadyRedeemed" };
    }

    return { valid: true, voucher };
  },

  // מימוש השובר באמצעות טרנזקציה למניעת שימוש כפול
  redeem: async (code: string, registrationId: string): Promise<void> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const dbRef = db;
    const normalized = code.trim().toUpperCase();

    const vouchersRef = collection(dbRef, "giftVouchers");
    const q = query(vouchersRef, where("code", "==", normalized));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error("Gift voucher not found");
    }

    const voucherDocRef = doc(dbRef, "giftVouchers", querySnapshot.docs[0].id);

    await runTransaction(dbRef, async (transaction) => {
      const voucherSnap = await transaction.get(voucherDocRef);
      if (!voucherSnap.exists()) {
        throw new Error("Gift voucher not found");
      }
      const voucher = voucherSnap.data() as GiftVoucher;
      if (voucher.status === "redeemed") {
        if (voucher.redeemedByRegistrationId === registrationId) {
          return;
        }
        throw new Error("Gift voucher already redeemed");
      }
      transaction.update(voucherDocRef, {
        status: "redeemed",
        redeemedAt: serverTimestamp(),
        redeemedByRegistrationId: registrationId,
      });
    });
  },

  getAll: async (): Promise<GiftVoucher[]> => {
    if (!db) {
      throw new Error(
        "Firebase not configured. Please set up your Firebase project."
      );
    }
    const vouchersRef = collection(db, "giftVouchers");
    const q = query(vouchersRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    })) as GiftVoucher[];
  },
};
